import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { DEFAULT_CONTENT, DEFAULT_PRODUCTS, DEFAULT_REVIEWS, DEFAULT_PROJECTS } from "@/lib/defaults";

const Ctx = createContext(null);

// Deep merge: server content overlays defaults (so missing keys keep their defaults)
function deepMerge(base, override) {
  if (override === undefined || override === null) return base;
  if (Array.isArray(base) || Array.isArray(override)) return override !== undefined ? override : base;
  if (typeof base !== "object" || typeof override !== "object") return override;
  const out = { ...base };
  for (const k of Object.keys(override)) out[k] = deepMerge(base[k], override[k]);
  return out;
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [products, setProducts] = useState(DEFAULT_PRODUCTS);
  const [reviews, setReviews] = useState(DEFAULT_REVIEWS);
  const [projects, setProjects] = useState(DEFAULT_PROJECTS);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [c, p, r, j] = await Promise.all([
        api.get("/content").catch(() => ({ data: {} })),
        api.get("/products").catch(() => ({ data: [] })),
        api.get("/reviews").catch(() => ({ data: [] })),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setContent(deepMerge(DEFAULT_CONTENT, c.data || {}));
      setProducts(Array.isArray(p.data) && p.data.length ? p.data : DEFAULT_PRODUCTS);
      setReviews(Array.isArray(r.data) && r.data.length ? r.data : DEFAULT_REVIEWS);
      setProjects(Array.isArray(j.data) && j.data.length ? j.data : DEFAULT_PROJECTS);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <Ctx.Provider value={{ content, products, reviews, projects, loaded, reload }}>
      {children}
    </Ctx.Provider>
  );
}

export function useContent() {
  const v = useContext(Ctx);
  if (!v) return { content: DEFAULT_CONTENT, products: DEFAULT_PRODUCTS, reviews: DEFAULT_REVIEWS, projects: DEFAULT_PROJECTS, loaded: false };
  return v;
}
